import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { api } from '../api/client';
import type { ReportSchedule, UpsertReportSchedule } from '../api/types';
import { Card, Badge, Alert, DataTable } from './ui';
import type { DataTableColumn } from './ui';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const FREQUENCIES: Array<{ value: ReportSchedule['frequency']; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly' },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Decode Keycloak roles from the access token (same approach as Layout.tsx).
function hasRole(token: string | undefined, role: string): boolean {
  if (!token) return false;
  try {
    const p = JSON.parse(atob(token.split('.')[1]));
    if (p.realm_access?.roles?.includes(role)) return true;
    for (const c of Object.values(p.resource_access || {})) {
      if ((c as { roles?: string[] }).roles?.includes(role)) return true;
    }
  } catch { /* ignore */ }
  return false;
}

// Inverse of billing-api's sastAnchorsToUtc — stored anchors are UTC, the form
// edits in SAST (UTC+2, no DST).
function utcToSast(hourUtc: number, dowUtc: number | null, domUtc: number | null) {
  let hour = hourUtc + 2;
  let shift = 0;
  if (hour >= 24) { hour -= 24; shift = 1; }
  return {
    hour,
    dow: dowUtc == null ? 1 : (((dowUtc + shift) % 7) + 7) % 7,
    dom: domUtc == null ? 1 : Math.min(31, domUtc + shift),
  };
}

function fmtSast(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-ZA', {
      timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

// Minute-of-day (SAST) helpers for the daily window pickers (30-min steps).
const minToLabel = (m: number) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const START_OPTIONS = Array.from({ length: 48 }, (_, i) => i * 30);          // 00:00 … 23:30
const END_OPTIONS = Array.from({ length: 48 }, (_, i) => (i + 1) * 30);      // 00:30 … 24:00 (1440)

interface Props { buildingId: string; buildingName?: string }

const blankForm = (email: string) => ({
  name: '',
  frequency: 'monthly' as ReportSchedule['frequency'],
  hour_of_day_sast: 8,
  day_of_week: 1,
  day_of_month: 1,
  recipients: email ? [email] : ([] as string[]),
  enabled: true,
  data_basis: 'billing' as ReportSchedule['data_basis'],
  window_start_min: 0,
  window_end_min: 1440,
});

export default function ReportSchedulePanel({ buildingId, buildingName }: Props) {
  const auth = useAuth();
  const email = auth.user?.profile?.email || '';
  const canManage = hasRole(auth.user?.access_token, 'billing:client') || hasRole(auth.user?.access_token, 'billing:admin');

  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create/edit modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<ReportSchedule | null>(null);
  const [form, setForm] = useState(blankForm(email));
  const [recipientDraft, setRecipientDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Send-now modal
  const [showSend, setShowSend] = useState(false);
  const [sendRecipients, setSendRecipients] = useState<string[]>([]);
  const [sendDraft, setSendDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState<string | null>(null);
  const [sendBasis, setSendBasis] = useState<'billing' | 'window'>('billing');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.listReportSchedules(buildingId);
      setSchedules(res.data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm(email));
    setRecipientDraft('');
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (s: ReportSchedule) => {
    const t = utcToSast(s.hour_of_day, s.day_of_week, s.day_of_month);
    setEditing(s);
    setForm({
      name: s.name,
      frequency: s.frequency,
      hour_of_day_sast: t.hour,
      day_of_week: t.dow,
      day_of_month: t.dom,
      recipients: [...s.recipients],
      enabled: s.enabled,
      data_basis: s.data_basis,
      window_start_min: s.window_start_min ?? 0,
      window_end_min: s.window_end_min ?? 1440,
    });
    setRecipientDraft('');
    setFormError(null);
    setShowModal(true);
  };

  const addTo = (val: string, current: string[], set: (r: string[]) => void, onErr: (m: string) => void): boolean => {
    const e = val.trim().toLowerCase();
    if (!e) return false;
    if (!EMAIL_RE.test(e)) { onErr(`Invalid email: ${val}`); return false; }
    if (!current.includes(e)) set([...current, e]);
    return true;
  };

  const handleSave = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setFormError(null);
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    if (form.recipients.length === 0) { setFormError('Add at least one recipient'); return; }
    const body: UpsertReportSchedule = {
      building_asset_id: buildingId,
      name: form.name.trim(),
      frequency: form.frequency,
      hour_of_day_sast: form.hour_of_day_sast,
      day_of_week: (form.frequency === 'weekly' || form.frequency === 'biweekly') ? form.day_of_week : null,
      day_of_month: form.frequency === 'monthly' ? form.day_of_month : null,
      recipients: form.recipients,
      enabled: form.enabled,
      data_basis: form.data_basis,
      window_start_min: form.data_basis === 'window' && form.frequency === 'daily' ? form.window_start_min : null,
      window_end_min: form.data_basis === 'window' && form.frequency === 'daily' ? form.window_end_min : null,
    };
    try {
      setSaving(true);
      if (editing) await api.updateReportSchedule(editing.id, body);
      else await api.createReportSchedule(body);
      setShowModal(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (s: ReportSchedule) => {
    if (!window.confirm(`Delete schedule "${s.name}"?`)) return;
    try { await api.deleteReportSchedule(s.id); await load(); }
    catch (e) { window.alert(e instanceof Error ? e.message : 'Failed to delete'); }
  };

  const handleRunNow = async (s: ReportSchedule) => {
    try {
      await api.runReportScheduleNow(s.id);
      window.alert('Report queued — it will arrive by email shortly.');
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Failed to run');
    }
  };

  const openSend = () => {
    setSendRecipients(email ? [email] : []);
    setSendDraft('');
    setSendMsg(null);
    setSendBasis('billing');
    setShowSend(true);
  };

  const handleSend = async () => {
    setSendMsg(null);
    if (sendRecipients.length === 0) { setSendMsg('Add at least one recipient'); return; }
    try {
      setSending(true);
      await api.sendBuildingReportNow(buildingId, sendRecipients,
        sendBasis === 'window' ? { basis: 'window', frequency: 'daily', window_start_min: 0, window_end_min: 1440 } : undefined);
      setSendMsg('Report queued — it will arrive by email shortly.');
      setTimeout(() => setShowSend(false), 1600);
    } catch (e) {
      setSendMsg(e instanceof Error ? e.message : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const describe = (s: ReportSchedule): string => {
    const t = utcToSast(s.hour_of_day, s.day_of_week, s.day_of_month);
    const hh = `${String(t.hour).padStart(2, '0')}:00`;
    let base: string;
    if (s.frequency === 'daily') base = `Daily at ${hh}`;
    else if (s.frequency === 'weekly') base = `Weekly · ${DOW[t.dow]} at ${hh}`;
    else if (s.frequency === 'biweekly') base = `Every 2 weeks · ${DOW[t.dow]} at ${hh}`;
    else base = `Monthly · day ${t.dom} at ${hh}`;
    if (s.data_basis === 'window') {
      const win = s.frequency === 'daily' && s.window_start_min != null && s.window_end_min != null
        ? ` (${minToLabel(s.window_start_min)}–${minToLabel(s.window_end_min)})` : '';
      return `${base} · usage window${win}`;
    }
    return base;
  };

  // Not routed through <Badge>: each chip carries a dynamic email address
  // plus its own embedded remove button, not a fixed enum label — same
  // "doesn't fit Badge's shape" reasoning as the tenant-name chip in
  // BillRunDetail.tsx/BillDetail.tsx (wave 2b group D). Colours reused
  // verbatim from Badge's own `info` tone (sky-100/900, sky-900/100) so the
  // pair inherits an already contrast-tested profile instead of a new one.
  // The remove "x" no longer gets its own lighter shade (was blue-500,
  // 3.01:1 on blue-100 — already failed AA before this migration); it
  // inherits the label's colour and uses opacity for the hover affordance
  // instead, matching the hover:opacity-90 convention used for bg-accent
  // buttons elsewhere in this file.
  // Name identifies the schedule; Schedule (the human-readable cadence,
  // describe()) is the field a reader scans for next — this panel is a
  // per-building list that grows as more automated reports get configured.
  // The pre-existing "paused" chip moves from the Name cell into the
  // secondary Status column instead of staying inline: never put a Badge in
  // a primary column (DataTable's collapsing branch has no overflow-x-auto
  // fallback below lg, so a badge that can't wrap can't be contained).
  // rowActions (Run now/Edit/Delete) stays role-gated on canManage, same as
  // the original conditional Actions <th>.
  const scheduleColumns: DataTableColumn<ReportSchedule>[] = [
    {
      key: 'name',
      label: 'Name',
      priority: 'primary',
      render: (s) => <span className="font-medium text-content">{s.name}</span>,
    },
    {
      key: 'schedule',
      label: 'Schedule',
      priority: 'primary',
      render: (s) => <span className="text-content">{describe(s)}</span>,
    },
    {
      key: 'recipients',
      label: 'Recipients',
      priority: 'secondary',
      render: (s) => <span className="text-content-muted">{s.recipients.length} recipient{s.recipients.length === 1 ? '' : 's'}</span>,
    },
    {
      key: 'next_run',
      label: 'Next run',
      priority: 'secondary',
      render: (s) => <span className="text-content-muted">{s.enabled ? fmtSast(s.next_run_at) : '—'}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      priority: 'secondary',
      // The `sent` branch was a bare `text-green-600` (#16a34a): 3.30:1 on
      // light `surface`/`surfaceRaised` (#ffffff), below the 4.5:1 AA floor
      // for normal text, and with no `dark:` counterpart it was invisible to
      // the dark-mode sweeps — a LIGHT-mode failure. Now matched to the
      // shape of the `error` branch beside it (a light shade that passes +
      // a `dark:` -400 counterpart), one stop darker in light because green
      // needs it where red did not: green-700 (#15803d) is 5.02:1 on white
      // and 4.77:1 on `surfaceSunken`, where green-600 was only 3.30/3.13.
      // dark green-400 (#4ade80) is 8.71:1 on a Card. Not routed through
      // Badge: the cell appends ` · <timestamp>` to the status word, and
      // Badge is `whitespace-nowrap` — chipping a status+timestamp string
      // would reintroduce the unbreakable-content-in-a-cell hazard. Both
      // shades are now policed as `statusOkText`/`statusErrorText` in
      // src/test/contrast.test.ts rather than hand-verified here.
      render: (s) => (
        <>
          {!s.enabled && <Badge tone="neutral" className="mr-2">paused</Badge>}
          {s.last_status
            ? <span className={s.last_status === 'error' ? 'text-red-600 dark:text-red-400' : s.last_status === 'sent' ? 'text-green-700 dark:text-green-400' : 'text-content-muted'}>{s.last_status}{s.last_run_at ? ` · ${fmtSast(s.last_run_at)}` : ''}</span>
            : <span className="text-content-muted">never run</span>}
        </>
      ),
    },
  ];

  const recipientChips = (list: string[], remove: (e: string) => void) => (
    <div className="flex flex-wrap gap-1.5">
      {list.map(r => (
        <span key={r} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-sky-100 text-sky-900 dark:bg-sky-900 dark:text-sky-100 rounded-full">
          {r}
          <button type="button" onClick={() => remove(r)} className="opacity-60 hover:opacity-100">×</button>
        </span>
      ))}
      {list.length === 0 && <span className="text-xs text-content-muted">No recipients yet</span>}
    </div>
  );

  return (
    <Card className="mt-8">
      <div className="px-5 py-4 border-b border-border-token flex items-center gap-3">
        <div>
          <h3 className="text-base font-semibold text-content">Automated Reports</h3>
          <p className="text-xs text-content-muted">Email this report on a schedule. Times shown in SAST (UTC+2).</p>
        </div>
        {canManage && (
          <div className="ml-auto flex items-center gap-2">
            <button onClick={openSend} className="px-3 py-1.5 text-sm font-medium text-content bg-surface-sunken rounded-lg hover:bg-border-token">
              Send now
            </button>
            <button onClick={openCreate} className="px-3 py-1.5 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:opacity-90">
              New schedule
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="p-6 text-sm text-content-muted">Loading schedules…</div>
      ) : error ? (
        <div className="p-6 text-sm text-red-700 dark:text-red-400">{error}</div>
      ) : schedules.length === 0 ? (
        <div className="p-8 text-center text-sm text-content-muted">
          No automated reports yet.{canManage ? ' Click “New schedule” to set one up.' : ''}
        </div>
      ) : (
        <DataTable
          data={schedules}
          columns={scheduleColumns}
          rowKey={(s) => s.id}
          rowActions={canManage ? (s) => (
            /* flex-wrap: three tap-sized targets in one cell can exceed the
               actions column at 375px; without it they overflow instead of
               stacking.
               `lg:min-h-0`: 44px is a TOUCH floor, not a desktop target — in
               a table row it drives row height and costs rows-per-screen.
               Full rationale on Tenants.tsx's rowActions; escape documented
               at tailwind.config.js:103. Especially load-bearing here, where
               THREE stacked-on-wrap 44px buttons share one cell. */
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button onClick={() => handleRunNow(s)} className="tap-target lg:min-h-0 text-xs text-content-muted hover:text-content">Run now</button>
              <button onClick={() => openEdit(s)} className="tap-target lg:min-h-0 text-xs text-accent hover:opacity-90">Edit</button>
              <button onClick={() => handleDelete(s)} className="tap-target lg:min-h-0 text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300">Delete</button>
            </div>
          ) : undefined}
        />
      )}

      {/* Create / edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-raised rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-border-token">
              <h3 className="text-lg font-semibold text-content">{editing ? 'Edit schedule' : 'New schedule'}</h3>
              <p className="text-xs text-content-muted">{buildingName}</p>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              {formError && <Alert tone="danger">{formError}</Alert>}

              <div>
                <label className="block text-sm font-medium text-content mb-1">Name</label>
                <input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Monthly landlord report"
                  className="w-full px-3 py-2 border border-border-token rounded-lg text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Frequency</label>
                  <select
                    value={form.frequency}
                    onChange={e => setForm({ ...form, frequency: e.target.value as ReportSchedule['frequency'] })}
                    className="w-full px-3 py-2 border border-border-token rounded-lg text-sm"
                  >
                    {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Send at (SAST)</label>
                  <select
                    value={form.hour_of_day_sast}
                    onChange={e => setForm({ ...form, hour_of_day_sast: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-border-token rounded-lg text-sm"
                  >
                    {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                  </select>
                </div>
              </div>

              {/* Data basis: billing period vs time window (energy report) */}
              <div>
                <label className="block text-sm font-medium text-content mb-1">Report data</label>
                <select
                  value={form.data_basis}
                  onChange={e => setForm({ ...form, data_basis: e.target.value as ReportSchedule['data_basis'] })}
                  className="w-full px-3 py-2 border border-border-token rounded-lg text-sm"
                >
                  <option value="billing">Billing period (last finalized bill run)</option>
                  <option value="window">Time window (live usage summary)</option>
                </select>
                {form.data_basis === 'window' && (
                  <p className="text-xs text-content-muted mt-1">
                    Consumption + cost for the {form.frequency === 'daily' ? 'chosen hours' : form.frequency === 'monthly' ? 'last complete month' : form.frequency === 'biweekly' ? 'last 2 complete weeks' : 'last complete week'}. Monthly capacity &amp; fixed charges are excluded.
                  </p>
                )}
              </div>

              {form.data_basis === 'window' && form.frequency === 'daily' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-content mb-1">From (SAST)</label>
                    <select value={form.window_start_min} onChange={e => setForm({ ...form, window_start_min: Number(e.target.value) })} className="w-full px-3 py-2 border border-border-token rounded-lg text-sm">
                      {START_OPTIONS.map(m => <option key={m} value={m}>{minToLabel(m)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-content mb-1">To (SAST)</label>
                    <select value={form.window_end_min} onChange={e => setForm({ ...form, window_end_min: Number(e.target.value) })} className="w-full px-3 py-2 border border-border-token rounded-lg text-sm">
                      {END_OPTIONS.map(m => <option key={m} value={m}>{m === 1440 ? '24:00' : minToLabel(m)}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {(form.frequency === 'weekly' || form.frequency === 'biweekly') && (
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Day of week</label>
                  <select
                    value={form.day_of_week}
                    onChange={e => setForm({ ...form, day_of_week: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-border-token rounded-lg text-sm"
                  >
                    {DOW.map((d, i) => <option key={i} value={i}>{d}</option>)}
                  </select>
                </div>
              )}

              {form.frequency === 'monthly' && (
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Day of month</label>
                  <select
                    value={form.day_of_month}
                    onChange={e => setForm({ ...form, day_of_month: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-border-token rounded-lg text-sm"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <p className="text-xs text-content-muted mt-1">Days past a month’s length fall on the last day (e.g. 31 → Feb 28).</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-content mb-1">Recipients</label>
                {recipientChips(form.recipients, r => setForm({ ...form, recipients: form.recipients.filter(x => x !== r) }))}
                <div className="flex gap-2 mt-2">
                  <input
                    value={recipientDraft}
                    onChange={e => setRecipientDraft(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (addTo(recipientDraft, form.recipients, r => setForm({ ...form, recipients: r }), setFormError)) setRecipientDraft('');
                      }
                    }}
                    placeholder="name@example.com"
                    className="flex-1 px-3 py-2 border border-border-token rounded-lg text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => { if (addTo(recipientDraft, form.recipients, r => setForm({ ...form, recipients: r }), setFormError)) setRecipientDraft(''); }}
                    className="px-3 py-2 text-sm bg-surface-sunken rounded-lg hover:bg-border-token"
                  >Add</button>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-content">
                <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />
                Active
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-content bg-surface-sunken rounded-lg hover:bg-border-token">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm text-accent-fg bg-accent rounded-lg hover:opacity-90 disabled:opacity-60">
                  {saving ? 'Saving…' : (editing ? 'Save changes' : 'Create schedule')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Send-now modal */}
      {showSend && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-surface-raised rounded-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-border-token">
              <h3 className="text-lg font-semibold text-content">Send report now</h3>
              <p className="text-xs text-content-muted">{buildingName} — emails the current report as a PDF.</p>
            </div>
            <div className="p-6 space-y-4">
              {sendMsg && <Alert tone="info">{sendMsg}</Alert>}
              <div>
                <label className="block text-sm font-medium text-content mb-1">Report data</label>
                <select
                  value={sendBasis}
                  onChange={e => setSendBasis(e.target.value as 'billing' | 'window')}
                  className="w-full px-3 py-2 border border-border-token rounded-lg text-sm"
                >
                  <option value="billing">Billing period (last finalized bill run)</option>
                  <option value="window">Yesterday (live usage summary)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-content mb-1">Recipients</label>
                {recipientChips(sendRecipients, r => setSendRecipients(sendRecipients.filter(x => x !== r)))}
                <div className="flex gap-2 mt-2">
                  <input
                    value={sendDraft}
                    onChange={e => setSendDraft(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (addTo(sendDraft, sendRecipients, setSendRecipients, setSendMsg)) setSendDraft('');
                      }
                    }}
                    placeholder="name@example.com"
                    className="flex-1 px-3 py-2 border border-border-token rounded-lg text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => { if (addTo(sendDraft, sendRecipients, setSendRecipients, setSendMsg)) setSendDraft(''); }}
                    className="px-3 py-2 text-sm bg-surface-sunken rounded-lg hover:bg-border-token"
                  >Add</button>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowSend(false)} className="px-4 py-2 text-sm text-content bg-surface-sunken rounded-lg hover:bg-border-token">Cancel</button>
                <button type="button" onClick={handleSend} disabled={sending} className="px-4 py-2 text-sm text-accent-fg bg-accent rounded-lg hover:opacity-90 disabled:opacity-60">
                  {sending ? 'Sending…' : 'Send now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
